import React from 'react';
import { PieChart, Pie, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';

const InsightFormatter = ({ structuredData }) => {
  const cleanGeminiResponse = (rawText) => {
    return rawText
      .replace(/^\s*```json\s*/i, '')
      .replace(/^\s*```/, '')
      .replace(/\s*```$/, '')
      .trim();
  };

  if (!structuredData) return <p style={{ color: '#ccc' }}>Generating AI Insights</p>;

  const cleanedResponse = typeof structuredData === 'string'
    ? JSON.parse(cleanGeminiResponse(structuredData))
    : structuredData;

  const {
    performanceSummary = {},
    keyObservations = [],
    generalRecommendations = [],
    specificRecommendations = [],
    overallStrategy = []
  } = cleanedResponse;

  const ulStyle = {
    listStylePosition: 'inside',
    color: '#e2e8f0',
    marginTop: '0.5rem',
    lineHeight: '1.75rem'
  };

  const pieData = [
    {
      name: 'Overperforming',
      value: performanceSummary.overperforming?.length || 0,
      activities: performanceSummary.overperforming?.map(a => a.activity) || []
    },
    {
      name: 'Underperforming',
      value: performanceSummary.underperforming?.length || 0,
      activities: performanceSummary.underperforming?.map(a => a.activity) || []
    }
  ];

  const COLORS = ['#4ade80', '#facc15'];

  const renderCustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div style={{ background: '#1e293b', color: '#f1f5f9', border: '1px solid #334155', borderRadius: '0.5rem', padding: '1rem' }}>
          <strong>{data.name}</strong>
          <ul style={{ paddingLeft: '1rem', marginTop: '0.5rem' }}>
            {data.activities.map((act, i) => <li key={i}>{act}</li>)}
          </ul>
        </div>
      );
    }
    return null;
  };

  return (
    <div style={{ maxWidth: '800px', margin: '2rem auto', padding: '2rem', backgroundColor: '#0f172a', color: '#f1f5f9', borderRadius: '1rem', boxShadow: '0 4px 15px rgba(0, 0, 0, 0.5)' }}>
      <h2 style={{ fontSize: '2rem', fontWeight: 'bold', color: '#f472b6', marginBottom: '1.5rem' }}>🏃‍♂️ Personalized Fitness AI Insights</h2>

      <section style={{ marginBottom: '2rem' }}>
        <h3 style={{ fontSize: '1.5rem', fontWeight: '600', color: '#38bdf8', marginBottom: '1rem' }}>📊 Activity Distribution</h3>
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie
              data={pieData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius={100}
              label
            >
              {pieData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip content={renderCustomTooltip} />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </section>

      <section>
        <h3 style={{ fontSize: '1.5rem', fontWeight: '600', color: '#4ade80', marginBottom: '0.5rem' }}>✅ Activities You’re Excelling In</h3>
        <p style={{ color: '#cbd5e1', marginBottom: '0.5rem' }}>Keep up the great work in these activities — consider using them as motivation boosters!</p>
        <ul style={ulStyle}>
          {(performanceSummary.overperforming || []).map((item, i) => (
            <li key={i}><strong>{item.activity}</strong>: {item.details}</li>
          ))}
        </ul>
      </section>

      <section style={{ marginTop: '1.5rem' }}>
        <h3 style={{ fontSize: '1.5rem', fontWeight: '600', color: '#facc15', marginBottom: '0.5rem' }}>⚠️ Activities That Need Improvement</h3>
        <p style={{ color: '#cbd5e1', marginBottom: '0.5rem' }}>These areas show room for growth — don't be discouraged, use the tips below to improve.</p>
        <ul style={ulStyle}>
          {(performanceSummary.underperforming || []).map((item, i) => (
            <li key={i}><strong>{item.activity}</strong>: {item.details}</li>
          ))}
        </ul>
      </section>

      <section style={{ marginTop: '1.5rem' }}>
        <h3 style={{ fontSize: '1.5rem', fontWeight: '600', color: '#f472b6', marginBottom: '0.5rem' }}>🧠 Key Observations</h3>
        <p style={{ color: '#cbd5e1', marginBottom: '0.5rem' }}>These insights highlight your current patterns and performance consistency.</p>
        <ul style={ulStyle}>
          {keyObservations.map((obs, i) => (
            <li key={i}>{obs}</li>
          ))}
        </ul>
      </section>

      <section style={{ marginTop: '1.5rem' }}>
        <h3 style={{ fontSize: '1.5rem', fontWeight: '600', color: '#60a5fa', marginBottom: '0.5rem' }}>📌 General Recommendations</h3>
        <p style={{ color: '#cbd5e1', marginBottom: '0.5rem' }}>These tips apply to your overall routine and can help build a stronger foundation.</p>
        <ul style={ulStyle}>
          {generalRecommendations.map((rec, i) => (
            <li key={i}>{rec}</li>
          ))}
        </ul>
      </section>

      <section style={{ marginTop: '1.5rem' }}>
        <h3 style={{ fontSize: '1.5rem', fontWeight: '600', color: '#22d3ee', marginBottom: '0.5rem' }}>🎯 Specific Recommendations by Activity</h3>
        {specificRecommendations.map((act, i) => (
          <details key={i} style={{ marginBottom: '1rem', border: '1px solid #334155', borderRadius: '0.5rem', padding: '1rem', backgroundColor: '#1e293b' }}>
            <summary style={{ fontWeight: '600', color: '#f8fafc', cursor: 'pointer' }}>{act.activity}</summary>
            <ul style={ulStyle}>
              {act.recommendations.map((point, j) => <li key={j}>{point}</li>)}
            </ul>
          </details>
        ))}
      </section>

      <section style={{ marginTop: '1.5rem' }}>
        <h3 style={{ fontSize: '1.5rem', fontWeight: '600', color: '#c084fc', marginBottom: '0.5rem' }}>🔁 Overall Strategy</h3>
        <p style={{ color: '#cbd5e1', marginBottom: '0.5rem' }}>Focus areas to guide your overall progress and help make improvements sustainable.</p>
        <ul style={ulStyle}>
          {overallStrategy.map((step, i) => (
            <li key={i}>{step}</li>
          ))}
        </ul>
      </section>
    </div>
  );
};

export default InsightFormatter;